import { useMemo, useCallback } from 'react';
import { DashboardEmployee, EmployeeZoneAlias } from '../types';

const STORAGE_KEY = 'employeeZoneSnapshot';

type ZoneSnapshot = Record<string, EmployeeZoneAlias>;

let processedEmployeesHash: string | null = null;
let cachedChangedAliases: Set<string> = new Set();
let snapshotSaved = false;

const createEmployeesHash = (employees: DashboardEmployee[]): string =>
  employees.map(e => `${e.employeeAlias}:${e.retentionRiskPriority}`).join('|');

const getStoredSnapshot = (): ZoneSnapshot | null => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEY);
    return storedData ? JSON.parse(storedData) : null;
  } catch {
    return null;
  }
};

const saveSnapshot = (snapshot: ZoneSnapshot): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {}
};

const buildCurrentSnapshot = (employees: DashboardEmployee[]): ZoneSnapshot =>
  employees.reduce((acc, employee) => {
    acc[employee.employeeAlias] = employee.retentionRiskPriority;
    return acc;
  }, {} as ZoneSnapshot);

const computeChangedAliases = (employees: DashboardEmployee[], previousSnapshot: ZoneSnapshot | null): Set<string> => {
  if (!previousSnapshot) {
    return new Set();
  }

  return new Set(
    employees
      .filter(employee => {
        const previousZone = previousSnapshot[employee.employeeAlias];
        return previousZone && previousZone !== employee.retentionRiskPriority;
      })
      .map(employee => employee.employeeAlias),
  );
};

export const useZoneChangeHighlight = (employees: DashboardEmployee[]) => {
  const changedAliases = useMemo(() => {
    if (!employees.length) {
      return new Set<string>();
    }

    const employeesHash = createEmployeesHash(employees);

    if (employeesHash === processedEmployeesHash) {
      return cachedChangedAliases;
    }

    const previousSnapshot = getStoredSnapshot();
    const currentSnapshot = buildCurrentSnapshot(employees);
    const changed = computeChangedAliases(employees, previousSnapshot);

    if (!snapshotSaved) {
      saveSnapshot(currentSnapshot);
      snapshotSaved = true;
    }

    processedEmployeesHash = employeesHash;
    cachedChangedAliases = changed;

    return changed;
  }, [employees]);

  const hasZoneChanged = useCallback(
    (employeeAlias: string): boolean => changedAliases.has(employeeAlias),
    [changedAliases],
  );

  return { hasZoneChanged };
};
